import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, ChevronLeft, User, BookOpen, Star, ArrowRight, Calendar, Plus, PartyPopper, Pencil, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('ar-OM', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return '—'; }
};

const AdminReaders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [readers, setReaders] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedReader, setSelectedReader] = useState(null);
  const [readerDetail, setReaderDetail] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [missedAudit, setMissedAudit] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isMaster = user?.admin_role === 'مطلق';

  const handleDeleteReader = async () => {
    if (!selectedReader || !readerDetail?.reader) return;
    const r = readerDetail.reader;
    const label = `${r.name || ''} (#${selectedReader})`;
    const first = window.confirm(
      `تحذير: سيتم حذف الحساب «${label}» وكل بياناته المرتبطة (التقارير، السلاسل، الأوسمة، الملاحظات...). هذا الإجراء لا يمكن التراجع عنه.\n\nهل أنت متأكد؟`
    );
    if (!first) return;
    const confirm2 = window.prompt(`للتأكيد، اكتب رقم القارئ «${selectedReader}»:`);
    if (String(confirm2 || '').trim() !== String(selectedReader)) {
      toast.info('تم إلغاء الحذف');
      return;
    }
    setDeleting(true);
    try {
      const { data } = await api.delete(`/admin/readers/${encodeURIComponent(selectedReader)}`);
      const n = Object.values(data?.deletions || {}).reduce((a, b) => a + Number(b || 0), 0);
      toast.success(`تم حذف الحساب وجميع بياناته (${n} سجلاً)`);
      setSelectedReader(null);
      setReaderDetail(null);
      if (searchParams.get('reader')) {
        const next = new URLSearchParams(searchParams);
        next.delete('reader');
        setSearchParams(next, { replace: true });
      }
      await loadReaders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل حذف الحساب');
    } finally {
      setDeleting(false);
    }
  };

  const runNicknameBackfill = async () => {
    const ok = window.confirm(
      'سيتم فحص جميع القارئات وإعطاء لقب فريد بصيغة «القارئة_NNN» لكل قارئة، مع فك أي تكرار في الألقاب. هل ترغب بالمتابعة؟'
    );
    if (!ok) return;
    setBackfillLoading(true);
    try {
      const { data } = await api.post('/admin/backfill-female-titles');
      const n = data?.updated ?? 0;
      toast.success(n > 0 ? `تم تحديث ${n} قارئة بألقاب فريدة` : 'كل الألقاب سليمة — لا حاجة للتحديث');
      // Refresh the list so new titles show immediately.
      try {
        const { data: readersData } = await api.get('/admin/readers');
        setReaders(readersData || []);
      } catch {/* ignore refresh error */}
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل إعادة تعيين الألقاب');
    } finally {
      setBackfillLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.is_admin) { navigate('/login'); return; }
    loadReaders();
  }, []);

  // Deep-link support: /admin/readers?reader=NNN opens that reader's detail.
  useEffect(() => {
    const rn = searchParams.get('reader');
    if (rn && rn !== selectedReader) {
      loadReaderDetail(rn);
    }
  }, [searchParams]);

  const loadReaders = async () => {
    try {
      const res = await api.get('/admin/readers');
      setReaders(res.data);
    } catch (err) {
      toast.error('فشل تحميل القراء');
    } finally {
      setLoading(false);
    }
  };

  const loadReaderDetail = async (readerNumber) => {
    try {
      const [detail, audit] = await Promise.all([
        api.get(`/admin/reader/${readerNumber}`),
        api.get(`/admin/reader/${readerNumber}/missed-audit`).catch(() => ({ data: [] })),
      ]);
      setReaderDetail(detail.data);
      setMissedAudit(audit.data || []);
      setSelectedReader(readerNumber);
    } catch (err) {
      toast.error('فشل تحميل بيانات القارئ');
    }
  };

  const submitAdjust = async () => {
    const amt = parseInt(adjustAmount, 10);
    if (!amt || amt <= 0) {
      toast.error('أدخل قيمة موجبة');
      return;
    }
    setAdjustSaving(true);
    try {
      const res = await api.post(`/admin/reader/${selectedReader}/adjust-missed`, {
        amount: amt, reason: adjustReason.trim(),
      });
      toast.success('تم التعديل بنجاح');
      // Refresh
      setReaderDetail((d) => d ? { ...d, reader: { ...d.reader, missed_quota: res.data.missed_quota, manual_missed_adjustment: res.data.manual_missed_adjustment } } : d);
      const audit = await api.get(`/admin/reader/${selectedReader}/missed-audit`);
      setMissedAudit(audit.data || []);
      setAdjustOpen(false);
      setAdjustAmount('');
      setAdjustReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل التعديل');
    } finally {
      setAdjustSaving(false);
    }
  };

  const filtered = readers.filter((r) => {
    if (!search) return true;
    const q = search.trim();
    return (
      r.name?.includes(q) ||
      r.full_name?.includes(q) ||
      r.reader_number?.includes(q) ||
      (r.phone && String(r.phone).includes(q)) ||
      (r.title && r.title.includes(q)) ||
      (r.email && String(r.email).toLowerCase().includes(q.toLowerCase()))
    );
  });

  // Sort: challenge-completed readers first, then by achievements desc, then by name.
  const meetsChallenge = (r) => {
    const ch = Number(r.challenge || 0);
    const ach = Number(r.total_achievements || 0);
    return ch > 0 && ach >= ch;
  };
  const sorted = [...filtered].sort((a, b) => {
    const aDone = meetsChallenge(a) ? 1 : 0;
    const bDone = meetsChallenge(b) ? 1 : 0;
    if (aDone !== bDone) return bDone - aDone;
    return (Number(b.total_achievements || 0) - Number(a.total_achievements || 0));
  });

  const toggleCongratulated = async (e, reader) => {
    e.stopPropagation();
    try {
      const res = await api.post(`/admin/reader/${reader.reader_number}/toggle-congratulated`);
      const marked = res.data.congratulated;
      setReaders((prev) => prev.map((x) => {
        if (x.reader_number !== reader.reader_number) return x;
        const list = new Set(x.congratulated_by || []);
        if (marked) list.add(user.reader_number); else list.delete(user.reader_number);
        return { ...x, congratulated_by: Array.from(list) };
      }));
      toast.success(marked ? 'تم تعليم "تمت التهنئة"' : 'تم إلغاء العلامة');
    } catch { toast.error('فشل التحديث'); }
  };

  const isCongratulated = (reader) => (reader.congratulated_by || []).includes(user?.reader_number);

  const openEdit = () => {
    const r = readerDetail?.reader || {};
    setEditForm({
      reader_number: r.reader_number || '',
      name: r.name || '',
      full_name: r.full_name || '',
      phone: r.phone || '',
      email: r.email || '',
      title: r.title || '',
      challenge: r.challenge || 0,
      status: r.status || '',
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    setEditSaving(true);
    try {
      const originalNumber = readerDetail.reader.reader_number;
      const res = await api.put(`/admin/reader/${originalNumber}`, editForm);
      toast.success('تم تحديث بيانات القارئ');
      const newNumber = res.data.reader_number || originalNumber;
      // Refresh detail (use possibly-new reader_number)
      const detail = await api.get(`/admin/reader/${newNumber}`);
      setReaderDetail(detail.data);
      setSelectedReader(newNumber);
      // Refresh readers list
      await loadReaders();
      setEditOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل التحديث');
    } finally {
      setEditSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
      </div>
    );
  }

  // Reader detail view
  if (selectedReader && readerDetail) {
    const r = readerDetail.reader;
    return (
      <div className="space-y-6" data-testid="reader-detail">
        <div className="flex items-center justify-between gap-2">
          <Button variant="outline" onClick={() => {
            setSelectedReader(null);
            setReaderDetail(null);
            if (searchParams.get('reader')) {
              const next = new URLSearchParams(searchParams);
              next.delete('reader');
              setSearchParams(next, { replace: true });
            }
          }}>
            <ArrowRight className="w-4 h-4 ml-2" />
            رجوع للقائمة
          </Button>
          <div className="flex items-center gap-2">
            <Button
              onClick={openEdit}
              className="bg-primary text-white hover:bg-primary/90"
              data-testid="edit-reader-btn"
            >
              <Pencil className="w-4 h-4 ml-2" />
              تعديل البيانات
            </Button>
            {isMaster && (
              <Button
                onClick={handleDeleteReader}
                disabled={deleting}
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                data-testid="delete-reader-btn"
                title="حذف الحساب وكل بياناته نهائياً"
              >
                <Trash2 className="w-4 h-4 ml-2" />
                {deleting ? 'جارٍ الحذف...' : 'حذف الحساب'}
              </Button>
            )}
          </div>
        </div>

        <Card className="p-8">
          <div className="flex items-center gap-6 mb-6">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
              <User className="w-10 h-10 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-accent">{r.full_name || r.name}</h2>
              <p className="text-muted-foreground text-lg">رقم: {r.reader_number} · {r.gender}{r.title ? ` · اللقب: ${r.title}` : ''}</p>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                <Calendar className="w-4 h-4" />
                <span>تاريخ الانضمام: {formatDate(r.join_date)}</span>
              </p>
            </div>
          </div>

          {/* Personal Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 bg-muted/20 rounded-lg p-4 mb-6" data-testid="reader-info-grid">
            <div>
              <p className="text-xs text-muted-foreground">الاسم المختصر</p>
              <p className="font-semibold" data-testid="info-name">{r.name || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">الاسم الثلاثي</p>
              <p className="font-semibold" data-testid="info-full-name">{r.full_name || '—'}</p>
            </div>
            {r.gender === 'أنثى' && (
              <div>
                <p className="text-xs text-muted-foreground">اللقب</p>
                <p className="font-semibold" data-testid="info-title">{r.title || '—'}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground">رقم الهاتف</p>
              <p className="font-semibold font-mono" dir="ltr" data-testid="info-phone">{r.phone || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">البريد الإلكتروني</p>
              <p className="font-semibold" dir="ltr" data-testid="info-email">{r.email || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">التحدي</p>
              <p className="font-semibold" data-testid="info-challenge">{r.challenge || 0}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">الصفة</p>
              <p className="font-semibold" data-testid="info-status">{r.status || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">الرقم القرائي</p>
              <p className="font-semibold font-mono" data-testid="info-reader-number">{r.reader_number}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-muted/30 p-4 rounded-lg text-center">
              <p className="text-2xl font-bold text-primary">{r.points}</p>
              <p className="text-sm text-muted-foreground">النقاط</p>
            </div>
            <div className="bg-muted/30 p-4 rounded-lg text-center">
              <p className="text-2xl font-bold text-secondary">{r.books_count}</p>
              <p className="text-sm text-muted-foreground">الكتب</p>
            </div>
            <div className="bg-muted/30 p-4 rounded-lg text-center">
              <p className="text-2xl font-bold text-accent">{r.total_pages}</p>
              <p className="text-sm text-muted-foreground">الصفحات</p>
            </div>
            <div className="bg-muted/30 p-4 rounded-lg text-center relative">
              <p className={`text-2xl font-bold ${r.missed_quota < 0 ? 'text-red-500' : 'text-emerald-600'}`} data-testid="missed-quota-value">{r.missed_quota}</p>
              <p className="text-sm text-muted-foreground">الورد الفائت</p>
              <button
                onClick={() => setAdjustOpen(true)}
                className="absolute top-2 left-2 p-1.5 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors"
                title="تعديل الورد الفائت"
                data-testid="open-adjust-missed"
              >
                <Plus className="w-3.5 h-3.5 text-primary" />
              </button>
            </div>
          </div>
          {r.last_report_date && (
            <p className="text-xs text-muted-foreground mt-3 text-center">آخر تقرير: {formatDate(r.last_report_date)}</p>
          )}
        </Card>

        {/* Edit Reader Modal */}
        {editOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setEditOpen(false)} data-testid="edit-reader-modal">
            <Card className="p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-accent mb-4">تعديل بيانات القارئ</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold mb-1">الرقم القرائي</label>
                  <Input
                    value={editForm.reader_number || ''}
                    onChange={(e) => setEditForm({ ...editForm, reader_number: e.target.value })}
                    data-testid="edit-reader-number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">الاسم المختصر</label>
                  <Input
                    value={editForm.name || ''}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    data-testid="edit-name"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold mb-1">الاسم الثلاثي</label>
                  <Input
                    value={editForm.full_name || ''}
                    onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                    data-testid="edit-full-name"
                  />
                </div>
                {r.gender === 'أنثى' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-1">اللقب</label>
                    <Input
                      value={editForm.title || ''}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      placeholder="اللقب المستعار للنشر العام"
                      data-testid="edit-title"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold mb-1">رقم الهاتف</label>
                  <Input
                    value={editForm.phone || ''}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    dir="ltr"
                    data-testid="edit-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">البريد الإلكتروني</label>
                  <Input
                    type="email"
                    value={editForm.email || ''}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    dir="ltr"
                    data-testid="edit-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">التحدي</label>
                  <Input
                    type="number"
                    value={editForm.challenge ?? 0}
                    onChange={(e) => setEditForm({ ...editForm, challenge: parseInt(e.target.value || '0', 10) })}
                    data-testid="edit-challenge"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">الصفة</label>
                  <Input
                    value={editForm.status || ''}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    placeholder="مثال: قارئ ذهبي"
                    data-testid="edit-status"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button onClick={submitEdit} disabled={editSaving} className="flex-1" data-testid="submit-edit-reader">
                  {editSaving ? '...جارٍ الحفظ' : 'حفظ التعديلات'}
                </Button>
                <Button variant="outline" onClick={() => setEditOpen(false)}>إلغاء</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                ملاحظة: تغيير الرقم القرائي سيحدّث جميع تقارير هذا القارئ وسجلاته تلقائياً.
              </p>
            </Card>
          </div>
        )}

        {/* Adjust Missed Quota Modal */}
        {adjustOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setAdjustOpen(false)} data-testid="adjust-missed-modal">
            <Card className="p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-accent mb-4">تعديل الورد الفائت</h3>
              <p className="text-sm text-muted-foreground mb-4">أدخل قيمة موجبة لتُضاف إلى الورد الفائت (تُقلل الدَّين). القيمة الحالية: <span className="font-bold" data-testid="current-missed">{r.missed_quota}</span></p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-semibold mb-1">القيمة (رقم موجب)</label>
                  <Input
                    type="number" min="1"
                    value={adjustAmount}
                    onChange={(e) => setAdjustAmount(e.target.value)}
                    placeholder="مثال: 5"
                    data-testid="adjust-amount-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">السبب (اختياري)</label>
                  <Input
                    value={adjustReason}
                    onChange={(e) => setAdjustReason(e.target.value)}
                    placeholder="ظرف صحي، عذر شرعي..."
                    data-testid="adjust-reason-input"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <Button onClick={submitAdjust} disabled={adjustSaving} className="flex-1" data-testid="submit-adjust">
                  {adjustSaving ? '...جارٍ' : 'تعديل'}
                </Button>
                <Button variant="outline" onClick={() => setAdjustOpen(false)}>إلغاء</Button>
              </div>
              {missedAudit.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">سجل التعديلات (آخر {Math.min(missedAudit.length, 5)}):</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {missedAudit.slice(0, 5).map((a, i) => (
                      <div key={i} className="text-xs bg-muted/30 p-2 rounded flex justify-between">
                        <span>+{a.amount} {a.reason ? `— ${a.reason}` : ''}</span>
                        <span className="text-muted-foreground">{formatDate(a.ts)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {/* Reports */}
        <Card className="p-6">
          <h3 className="text-xl font-bold text-accent mb-4">التقارير ({readerDetail.reports.length})</h3>
          {readerDetail.reports.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">لا توجد تقارير</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-primary/20">
                    <th className="text-right py-3 px-2 font-bold text-accent">التاريخ</th>
                    <th className="text-right py-3 px-2 font-bold text-accent">التصنيف</th>
                    <th className="text-right py-3 px-2 font-bold text-accent">العنوان</th>
                    <th className="text-right py-3 px-2 font-bold text-accent">الصفحات</th>
                    <th className="text-right py-3 px-2 font-bold text-accent">مكتمل</th>
                  </tr>
                </thead>
                <tbody>
                  {readerDetail.reports.map((rp, i) => (
                    <tr key={i} className="border-b border-muted/30 hover:bg-muted/10">
                      <td className="py-3 px-2">{rp.date}</td>
                      <td className="py-3 px-2">{rp.classification}</td>
                      <td className="py-3 px-2">{rp.title}</td>
                      <td className="py-3 px-2 font-bold text-primary">{rp.pages_read}</td>
                      <td className="py-3 px-2">{rp.is_completed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Course Progress */}
        {readerDetail.course_progress.length > 0 && (
          <Card className="p-6">
            <h3 className="text-xl font-bold text-accent mb-4">تقدم المساقات</h3>
            <div className="space-y-3">
              {readerDetail.course_progress.map((cp, i) => (
                <div key={i} className="flex items-center justify-between bg-muted/20 p-4 rounded-lg">
                  <div>
                    <p className="font-semibold">{cp.bookKey}</p>
                    <p className="text-sm text-muted-foreground">مساق: {cp.courseId}</p>
                  </div>
                  <div className="text-left">
                    <p className="text-xl font-bold text-primary">{cp.lastPage} / {cp.totalPages}</p>
                    <p className="text-sm text-muted-foreground">{cp.isCompleted ? 'مكتمل' : 'جارٍ'}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-readers">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <h1 className="text-3xl font-bold text-accent">ادارة القراء</h1>
          <Button
            variant="outline"
            size="sm"
            onClick={runNicknameBackfill}
            disabled={backfillLoading}
            data-testid="reset-female-nicknames-btn"
            className="border-pink-300 text-pink-700 hover:bg-pink-50 hover:text-pink-800 gap-2"
            title="يفحص جميع القارئات ويعطي كل قارئة لقباً فريداً بصيغة «القارئة_NNN» ويزيل أي تكرار."
          >
            <Sparkles className="w-4 h-4" />
            {backfillLoading ? 'جارٍ التنفيذ...' : 'إعادة تعيين ألقاب القارئات'}
          </Button>
        </div>
        <div className="relative w-full md:w-96">
          <Search className="absolute right-3 top-3 w-5 h-5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث بالاسم/الرقم/الهاتف/البريد/اللقب..."
            className="pr-10 h-12"
            data-testid="search-readers"
          />
        </div>
      </div>

      <p className="text-muted-foreground">اجمالي: {sorted.length} قارئ · <span className="text-emerald-600 font-semibold">أنجزوا التحدي: {sorted.filter(meetsChallenge).length}</span></p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sorted.map((reader) => {
          const done = meetsChallenge(reader);
          const isFemale = reader.gender === 'أنثى';
          const cardBg = done
            ? (isFemale
                ? 'bg-gradient-to-br from-pink-50 to-rose-100/50 border-pink-300 hover:border-pink-400'
                : 'bg-gradient-to-br from-emerald-50 to-green-100/50 border-emerald-300 hover:border-emerald-400')
            : 'border-transparent hover:border-primary/30';
          const congratulated = isCongratulated(reader);
          return (
          <Card
            key={reader.reader_number}
            className={`p-5 cursor-pointer hover:shadow-lg transition-all border-2 ${cardBg}`}
            onClick={() => loadReaderDetail(reader.reader_number)}
            data-testid={`reader-card-${reader.reader_number}`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${done ? (isFemale ? 'bg-pink-200/60' : 'bg-emerald-200/60') : 'bg-primary/10'}`}>
                <User className={`w-6 h-6 ${done ? (isFemale ? 'text-pink-700' : 'text-emerald-700') : 'text-primary'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-bold text-accent truncate">{reader.name}</p>
                  {done && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 ${isFemale ? 'bg-pink-500 text-white' : 'bg-emerald-600 text-white'}`} data-testid="challenge-badge">
                      أنجز التحدي
                    </span>
                  )}
                  {reader.duplicate_count > 1 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold shrink-0 bg-red-100 text-red-700" title="يوجد أكثر من حساب بنفس الرقم القرائي" data-testid={`duplicate-badge-${reader.reader_number}`}>
                      مكرر ×{reader.duplicate_count}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{reader.reader_number} · تحدي {reader.challenge || 0}</p>
              </div>
              <div className="text-left shrink-0">
                <div className="flex items-center gap-1 text-primary">
                  <Star className="w-4 h-4" />
                  <span className="font-bold">{reader.points}</span>
                </div>
                <div className="flex items-center gap-1 text-muted-foreground text-sm">
                  <BookOpen className="w-3 h-3" />
                  <span>{reader.total_achievements || reader.books_count || 0}/{reader.challenge || 0}</span>
                </div>
              </div>
              {done && (
                <button
                  onClick={(e) => toggleCongratulated(e, reader)}
                  className={`shrink-0 p-2 rounded-full transition-all ${
                    congratulated
                      ? (isFemale
                          ? 'bg-pink-500 text-white shadow-lg shadow-pink-300 scale-110'
                          : 'bg-emerald-500 text-white shadow-lg shadow-emerald-300 scale-110')
                      : 'bg-white/70 text-muted-foreground hover:bg-white'
                  }`}
                  title={congratulated ? 'تم تهنئته — اضغط لإلغاء العلامة' : 'اضغط عند إرسال التهنئة'}
                  data-testid={`congrats-btn-${reader.reader_number}`}
                >
                  <PartyPopper className="w-4 h-4" />
                </button>
              )}
            </div>
          </Card>
        )})}
      </div>
    </div>
  );
};

export default AdminReaders;

